"use client";

import { useCustomEditor } from "@/providers/custom-editor-provider";
import { AtSignIcon, BoldIcon, ImageIcon, ItalicIcon, LinkIcon, UnderlineIcon } from "lucide-react";
import { useCallback } from "react";
import { EmojiButton } from "../../../extensions/emoji/emoji-option-button";
import { EditorOptionButton } from "./editor-option-button";

const CommentEditorOptions = ({ allowedFormats }: { allowedFormats: string[] }) => {
	const { editor, openMediaUploader } = useCustomEditor();

	const isEditorDisabled = !editor?.isEditable;

	const setLink = useCallback(() => {
		if (!editor) return;

		const previousUrl = editor.getAttributes("link").href;
		const url = window.prompt("URL", previousUrl);

		if (url === null) return;

		if (url === "") {
			editor.chain().focus().extendMarkRange("link").unsetLink().run();
			return;
		}

		editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
	}, [editor]);

	if (!editor) return null;

	return (
		<div className="flex items-center gap-px max-md:flex-wrap">
			<div className="flex items-center gap-px max-md:flex-wrap">
				<EditorOptionButton
					title="Images"
					icon={ImageIcon}
					onClick={() => openMediaUploader(false)}
					disabled={isEditorDisabled}
					hidden={!allowedFormats.includes("image")}
				/>

				<EditorOptionButton
					title="Link"
					icon={LinkIcon}
					onClick={setLink}
					active={editor.isActive("link")}
					hidden={!allowedFormats.includes("link")}
					disabled={isEditorDisabled}
				/>

				<EditorOptionButton
					icon={AtSignIcon}
					title="Profile Mention"
					onClick={() => editor.chain().focus().insertContent("@").run()}
					disabled={isEditorDisabled}
				/>

				<EmojiButton
					onEmojiSelect={(unicode) => editor.chain().focus().insertContent(unicode).run()}
				/>

				<EditorOptionButton
					title="Bold"
					icon={BoldIcon}
					active={editor.isActive("bold")}
					onClick={() => editor.chain().focus().toggleBold().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleBold().run()}
					hidden={!allowedFormats.includes("bold")}
				/>

				<EditorOptionButton
					title="Italic"
					icon={ItalicIcon}
					active={editor.isActive("italic")}
					onClick={() => editor.chain().focus().toggleItalic().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleItalic().run()}
					hidden={!allowedFormats.includes("italic")}
				/>

				<EditorOptionButton
					title="Underline"
					icon={UnderlineIcon}
					active={editor.isActive("underline")}
					onClick={() => editor.chain().focus().toggleUnderline().run()}
					disabled={isEditorDisabled || !editor.can().chain().focus().toggleUnderline().run()}
					hidden={!allowedFormats.includes("underline")}
				/>
			</div>
		</div>
	);
};

export default CommentEditorOptions;
